import { useState } from "react";
import { useLocation } from "react-router-dom";
import { LocaleLink as Link, useLanguageSwitcher } from "@/components/landing/LocaleLink";
import { stripLocale } from "@/lib/locale";
import { Menu, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { useScrolledPast } from "@/hooks/use-scrolled-past";
import { type LandingLang } from "@/lib/i18n-landing";
import { FEATURES } from "./data";
import { MARKETING_PAGES } from "./flags";
import { cn } from "@/lib/utils";

// Logos del kit Playium (lockup horizontal `LOGO_PLAYIUM-03/02`).
// Variante blanca: para fondo oscuro (hub sin scroll). Variante negra: para
// el estado scrolled (fondo blanco) y para las spokes con header sólido.
const LOGO_WHITE = "/images/LOGO_PLAYIUM-03-white.webp";
const LOGO_DARK = "/images/LOGO_PLAYIUM-02-black.webp";

const NAV_LINKS: Array<{ key: "pricing" | "chain" | "about"; to: string }> = [
  { key: "pricing", to: "/pricing" },
  { key: "chain", to: "/chain" },
  { key: "about", to: "/about-us" },
];

// "Sobre nosotros" siempre está; las spokes de marketing entran según su
// flag (ver flags.ts), para no enlazar una página que redirige al hub.
const VISIBLE_NAV_LINKS = NAV_LINKS.filter(
  (l) => l.key === "about" || MARKETING_PAGES[l.key],
);

const LANGS: LandingLang[] = ["es", "ca", "en"];

export default function Header() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const switchLang = useLanguageSwitcher();
  const [mobileOpen, setMobileOpen] = useState(false);

  const currentLang = (i18n.language as LandingLang) || "es";

  const isHub = stripLocale(location.pathname) === "/";
  const scrolled = useScrolledPast(40);
  const transparent = isHub && !scrolled;

  return (
    <nav
      className={cn("playium-header", transparent ? "is-transparent" : "is-solid")}
    >
      <div className="playium-header__inner">
        <Link
          to="/"
          className="playium-header__logo"
          aria-label="Playium"
        >
          <img loading="lazy"
            src={transparent ? LOGO_WHITE : LOGO_DARK}
            alt=""
            aria-hidden="true"
            className="playium-header__logo-img"
          />
        </Link>

        <div className="playium-header__nav">
          {MARKETING_PAGES.features && (
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="playium-header__link playium-header__link--dropdown">
                    {t("nav.features")}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <FeaturesDropdown />
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          )}

          {VISIBLE_NAV_LINKS.map((l) => (
            <Link key={l.key} to={l.to} className="playium-header__link">
              {t(`nav.${l.key}`)}
            </Link>
          ))}

          <Link to="/contact" className="playium-header__link playium-header__link--quiet">
            {t("nav.contact")}
          </Link>

          {/* Navegación COMPLETA (no SPA) a /platform/login: fuerza un cold
              load para que el manifest esté en el HTML parseado y Chrome
              ofrezca instalar la PWA en la pantalla de login. */}
          <a href="/platform/login" className="playium-header__cta">
            {t("nav.login")}
          </a>
        </div>

        <div className="playium-header__mobile">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                aria-label={t("nav.openMenu")}
                className="playium-header__mobile-trigger"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-sm p-0 flex flex-col">
              <SheetHeader className="px-6 pt-6 pb-2">
                <SheetTitle className="flex items-center gap-2.5 text-left">
                  <img loading="lazy" src={LOGO_DARK} alt="Playium" className="h-7 w-auto" />
                </SheetTitle>
              </SheetHeader>

              <div className="flex-1 overflow-y-auto px-6 py-4">
                {MARKETING_PAGES.features && (
                  <>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">
                      {t("nav.features")}
                    </p>
                    <ul className="space-y-0.5 mb-5 border-l-2 border-gray-100 pl-3">
                      {FEATURES.map((f) => {
                        const Icon = f.icon;
                        return (
                          <li key={f.key}>
                            <Link
                              to={`/features#${f.key}`}
                              onClick={() => setMobileOpen(false)}
                              className="flex items-center gap-2.5 py-2 text-sm text-gray-700 hover:text-playium-mint transition-colors"
                            >
                              <Icon className="h-4 w-4 text-playium-mint/80 shrink-0" />
                              {t(`features.${f.key}.label`)}
                            </Link>
                          </li>
                        );
                      })}
                      <li>
                        <Link
                          to="/features"
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-1.5 py-2 text-sm font-medium text-playium-mint hover:text-playium-mint/80"
                        >
                          {t("nav.allFeatures")} <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </li>
                    </ul>
                  </>
                )}

                <ul className="space-y-0.5">
                  {VISIBLE_NAV_LINKS.map((l) => (
                    <li key={l.key}>
                      <Link
                        to={l.to}
                        onClick={() => setMobileOpen(false)}
                        className="block py-3 text-base font-semibold text-gray-900"
                      >
                        {t(`nav.${l.key}`)}
                      </Link>
                    </li>
                  ))}
                  <li>
                    <Link
                      to="/contact"
                      onClick={() => setMobileOpen(false)}
                      className="block py-3 text-base font-semibold text-gray-900"
                    >
                      {t("nav.contact")}
                    </Link>
                  </li>
                </ul>
              </div>

              <div className="p-6 border-t border-gray-100 space-y-4">
                <div
                  role="group"
                  aria-label={t("nav.language")}
                  className="flex items-center gap-1 bg-gray-100 rounded-full p-1"
                >
                  {LANGS.map((l) => (
                    <button
                      key={l}
                      onClick={() => switchLang(l)}
                      aria-pressed={currentLang === l}
                      aria-label={l.toUpperCase()}
                      className={`flex-1 py-2 text-sm font-semibold rounded-full transition-all ${
                        currentLang === l
                          ? "bg-white shadow-sm text-gray-900"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {l.toUpperCase()}
                    </button>
                  ))}
                </div>

                <a href="/platform/login" onClick={() => setMobileOpen(false)} className="block">
                  <Button
                    size="lg"
                    className="w-full rounded-full bg-[#092348] text-white hover:bg-[#092348]/90"
                  >
                    {t("nav.login")}
                  </Button>
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
}

function FeaturesDropdown() {
  const { t } = useTranslation();
  return (
    <div className="p-4 w-[640px]">
      <div className="grid grid-cols-2 gap-1">
        {FEATURES.map((f) => {
          const Icon = f.icon;
          return (
            <NavigationMenuLink asChild key={f.key}>
              <Link
                to={`/features#${f.key}`}
                className="flex items-start gap-3 rounded-lg p-3 hover:bg-playium-mint/10 transition-colors group"
              >
                <div className="h-9 w-9 rounded-lg bg-playium-mint/10 flex items-center justify-center shrink-0 group-hover:bg-playium-mint/20 transition-colors">
                  <Icon className="h-4 w-4 text-playium-mint" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 leading-tight">
                    {t(`features.${f.key}.label`)}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed line-clamp-2">
                    {t(`features.${f.key}.desc`)}
                  </p>
                </div>
              </Link>
            </NavigationMenuLink>
          );
        })}
      </div>
      <div className="mt-3 pt-3 border-t border-gray-100">
        <NavigationMenuLink asChild>
          <Link
            to="/features"
            className="flex items-center gap-1.5 text-sm font-semibold text-playium-mint hover:text-playium-mint/80 transition-colors px-3 py-1.5"
          >
            {t("nav.allFeatures")} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </NavigationMenuLink>
      </div>
    </div>
  );
}
